import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import reportClientId from 'scoreboard/reports/common/model/designer-client-id'
import escapePreserveLineBreaks from 'scoreboard/components/escape-preserve-line-breaks'
import unwrapModel from 'scoreboard/components/unwrap-model'
import MultiColumnTableStack from 'scoreboard/reports/common/table/multi-column-table-stack'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import isDateTimeDataType from 'scoreboard/components/is-date-time-data-type'
import reportConstants from 'scoreboard/reports/report-constants'
import coerceNilAggregatedValue from 'scoreboard/components/coerce-nil-aggregated-value'
import formatSummaryValue from 'scoreboard/datasets/overview/format-summary-value'
import formatValue, { shortenedAndUnshortened } from 'scoreboard/components/format-value'
import constants from 'scoreboard/components/constants'
import getAggregatedDataType from 'scoreboard/components/get-aggregated-data-type'
import DataType from 'scoreboard/datasets/model/data-type'
const { YES_NO, DATE, TEXT, DATASET_LINK, THRESHOLD_ID } = DataType
import { getTextVariation, rgbToHex } from 'scoreboard-colors'
import formatHtmlValue from 'scoreboard/components/format-html-value'
import { formatItem } from '../../related-items/related-items-list'
import enums from '../../components/enums'
import relatedItemTemplate from './related-item.hbs'
import {
	getValueForThresholdColoring
} from 'scoreboard/components/thresholds-and-colors'

const {
	COLORED_CELL_BG_OPACITY,
	TEXT_VARIATION_THRESHOLD,
	COLORED_BACKGROUND_TEXT_VARIATION_THRESHOLD
} = constants


function toAbbreviatedResult(value, unshortenedValue, columnAttributes) {
	const label = escapePreserveLineBreaks(i18n.get(columnAttributes.label))
	return `<span data-unshortened-header="${label}" data-unshortened-value="${unshortenedValue}">${value}</span>`
}


function applyThresholdColoring(cellData, dataType, value) {
	if (_.isNil(value) || (_.isString(value) && _.isEmpty(value))) { return }

	let { columnModel } = cellData
	let conditionalColorModel = columnModel.colorModel(dataType)
	if (!isNumericDataType(dataType) || dataType == YES_NO || _.isNil(value)) { return null }

	if (conditionalColorModel == null) {
		//if there is no color model available, determine the min/max range client side
		let minValue = _.get(columnModel, 'attributes.min', value)
		let maxValue = _.get(columnModel, 'attributes.max', value)
		_.set(columnModel, 'attributes.min', Math.min(minValue, value))
		_.set(columnModel, 'attributes.max', Math.max(maxValue, value))
		return null
	}

	value = getValueForThresholdColoring(value, dataType)

	let color = conditionalColorModel.color(value, COLORED_CELL_BG_OPACITY)
	let fontColor = conditionalColorModel.color(value, 1)
	let style = cellData.inlineStyle || {}
	cellData.style['color'] = true
	cellData.style['custom-color'] = true
	let textVariationThreshold = TEXT_VARIATION_THRESHOLD
	if (!cellData.style.text) {
		cellData.style['label-on-colored-background'] = true
		textVariationThreshold = COLORED_BACKGROUND_TEXT_VARIATION_THRESHOLD
	}
	let hex = rgbToHex([color.r, color.g, color.b])
	let textVariation = getTextVariation(hex, COLORED_CELL_BG_OPACITY,textVariationThreshold)
	cellData.style[textVariation] = true

	style['--custom-cell-color'] = color
	style['--custom-cell-font-color'] = fontColor
	cellData.inlineStyle = style
	return color
}

class ReportDataExtractor {

	constructor(reportModel) {
		this.reportModel = reportModel
	}

	/**
	 *  Columns contain attributes that generally specify properties used to
	 *  help format the value that needs to be display, in some cases though
	 *  properties on the item itself override the properties on the column, which
	 *  should be provided by this function.  Subclasses should override with
	 *  their domain specific behavior
	 */
	resolveContentAttributes(resultItem, columnModel) {
		return columnModel.attributes
	}

	toCellContents(resultItem, headerCell, dataRowIndex) {
		let columnModel = headerCell.column

		let {reportModel, stack, subtotal} = headerCell
		//the result object is the resultItem per cell referred to in table.js
		let result = {
			id: headerCell.id(),
			//pass along any style defined by the column
			style: _.extend({}, headerCell.columnStyle(), {
				'is-subtotal': !!subtotal
			}),
			columnModel
		}
		if (columnModel.get('newBlankColumn')) {
			return ''	//simulate new blank column
		}

		//note we must be careful not to mutate any of objects within this object
		//as we don't clone the data here for performance reasons
		let contentAttrs = this.resolveContentAttributes(resultItem, columnModel)
		result.contentAttributes = _.extend(contentAttrs, {
			aggregated: this.isAggregatingValuesFor(headerCell)
		})

		if (headerCell.tableStack instanceof MultiColumnTableStack) {
			return this.toBreakdownCellContents(result, resultItem, headerCell)
		}

		if (headerCell.stackAggregated) {
			const {value, dataType} = this.toNonRepeatedAggregatedValue(
				result, resultItem, headerCell
			)
			const aggregatedValue = value

			const aggregationType = columnModel.get('aggregationType')
			if (aggregationType === 'list-unique-values') {
				result.html = this._formatUniqueListedValues(
					aggregatedValue, columnModel
				)
			} else {
				result.html = this.applyValueFormatting(
					aggregatedValue, result.contentAttributes, {aggregated: true}
				)
			}
			result.rawValue = aggregatedValue

			let formattedAggregated = this._formatAggregatedValue(
				aggregatedValue, result.contentAttributes)
			if (!_.isNil(formattedAggregated)) {
				// If there is a specially formatted version of the aggregated
				// value, use it. Otherwise, default export formatting will
				// take care of it.
				result.exportValue = formattedAggregated
			}

			this._storeExportModel(result, aggregatedValue, result.contentAttributes)
			return result
		}

		//if this is a calculated value, use it as is
		if (columnModel.get('calculated')) {
			this.extractCalculatedData(result, resultItem, columnModel)
			return result
		}

		this.extractData(result, resultItem, headerCell)
		//if we're grouping and the value is blank, fill in with the word "Blank" #39310
		if (columnModel.has('groupingId') && _.isEmpty(result.html)) {
			reportConstants.formatForBlankLabel(result)
		}

		const hasDepth = !_.isNil(resultItem.depth)
		const isIndentHierarchy = !!contentAttrs.indentHierarchy
		const applyIndent = columnModel.showHierarchyIndentOption(reportModel) &&
			_.isNil(columnModel.validateHierarchyIndent(reportModel, stack))
		if (hasDepth && isIndentHierarchy && applyIndent) {
			const indentSize = resultItem.depth * reportConstants.INDENTATION_PIXELS
			result.html = `<div style='padding-left: ${indentSize}px'>${result.html}</div>`
		}

		return result
	}

	/**
	 * #59994 For values that have been aggregated, format them for export.
	 * A null return value will allow the default export formatting to be used.
	 *
	 * May be overridden by subclasses.
	 *
	 * @return {string|null}
	 * @abstract
	 */
	_formatAggregatedValue (value, columnAttributes) {}

	_formatUniqueListedValues(list, column) {
		let options = _.clone(unwrapModel(column))
		if (options.columnType == 'related-items') {
			options.escape = false
			options.includeCommas = false
			// prevent side-effects
			list = _.cloneDeep(list)
			_.each(list, formatItem)
		}
		list = _.map(list, v => this._formattedValueHtml(v, options))

		options = _.extend(options, {
			value: list,
			dataset: this.dataset,
			fieldId: options.datasetFieldId,
			treeItemsById: this._treeItemsById,
			shorten: options.abbreviated
		})
		return formatHtmlValue(options)
	}

	/**
	 * Returns the HTML to be displayed to the end user for a particular
	 * formatted value. Does not include the exportValue and rawValue
	 * @return {String}
	 */
	_formattedValueHtml(value, options) {
		return value
	}

	extractCalculatedData (cellData, resultItem, columnModel) {
		let columnClientId = reportClientId(columnModel)
		let dataType = columnModel.get('dataType')
		let calculatedValue = _.get(resultItem, `calculatedValuesByColumnClientId[${columnClientId}].value`, null)
		if (dataType == 'text') {
			cellData.html = _.isNil(calculatedValue) ?
				'' : escapePreserveLineBreaks(calculatedValue)
			return
		}
		cellData.rawValue = calculatedValue
		cellData.html = this.applyValueFormatting(calculatedValue, unwrapModel(columnModel))
		applyThresholdColoring(cellData, dataType, calculatedValue)
	}

	toBreakdownCellContents(result, resultItem, headerCell) {
		let { contentAttributes } = result
		let { breakdownColumnData } = headerCell

		/* RepeatBy structure in scorecard reports come in two "flavors" the first is when
		 * a column is grouped (aggregated) and then broken down by another field.
		 * In this scenario the resultItem will contain a "breakdownDataByStackClientId"
		 * property
		 *
		 * The second is a non-aggregated scored field, due to the differences on how
		 * the data is computed on the server, we use two different approaches to
		 * display that data.
		 */
		if (resultItem.breakdownDataByKey || contentAttributes.aggregated) {
			let valueAndFormatting = {}
			if (!_.isNil(resultItem.breakdownDataByKey) && !_.isNil(headerCell.breakdownDataKey)) {
				const breakdownData = resultItem.breakdownDataByKey[headerCell.breakdownDataKey()]
				valueAndFormatting = _.get(breakdownData, 'data', {})
			}
			this._aggregatedColumnDataContents(result, valueAndFormatting, contentAttributes)
			this._setBreakdownDataByStackClientIdStyles(result, valueAndFormatting,
				contentAttributes)
		} else {
			this.nonAggregatedPeriodBasedColumnDataContents(result, resultItem, headerCell)
		}

		return result
	}

	_aggregatedColumnDataContents(result, valueAndFormatting, contentAttributes) {
		const precision = _.get(valueAndFormatting, 'precision', contentAttributes.precision)
		const dataType = _.get(valueAndFormatting, 'dataType', contentAttributes.dataType)
		const opts = { aggregated: true, precision, dataType }
		const value = _.get(valueAndFormatting, 'value', '')
		result.html = this.applyValueFormatting(value, contentAttributes, opts)
		result.rawValue = value
		this._storeExportModel(result, value, contentAttributes, {dataType, precision})
	}

	/**
	 * Currently only used for scorecard score coloring.
	 * Sets report-type-specific styles
	 **/
	_setBreakdownDataByStackClientIdStyles() {}

	/**
	 * Handles formatting content when the column is in a non aggregated period based a repeat by.
	 * This only happens in a non-dataset report, for dataset reports this is a no-op
	 */
	nonAggregatedPeriodBasedColumnDataContents(result, resultItem, headerCell) {
	}

	isAggregatingValuesFor(headerCell) {
		const columnModel = headerCell.column
		return headerCell.reportModel.isAggregating() && !columnModel.isGrouping()
	}

	applyValueFormatting(value, contentAttributes = {}, overrides = {}) {
		let attrs = unwrapModel(contentAttributes)
		let resolvedAttrs = _.extend({}, attrs, overrides)

		//determine the datatype, anything set in the opts overrides the datatype
		//in the underlying attributes.
		let dataType = overrides.dataType || attrs.coercedDataType || attrs.dataType
		const { precision, abbreviated, aggregated, summaryType, aggregationType } = resolvedAttrs

		if (aggregated) {
			if (attrs.aggregationType === 'list-unique-values') {
				const formattingOptions = _.defaults({
					dataType,
					precision
				}, contentAttributes)
				return this._formatUniqueListedValues(value, formattingOptions)
			}

			//if we're aggregating, then the aggregation type may require a certain
			//data type so override the previous dataType with that
			dataType = getAggregatedDataType(dataType, aggregationType)

			// #39309 blanks should not appear in certain contexts
			value = coerceNilAggregatedValue(value, attrs)
		}

		if (summaryType && summaryType !== 'value' &&
			dataType != 'unique') {
			value =  formatSummaryValue(
				resolvedAttrs, value, this._summaryValueFormattingContext(
					contentAttributes
				)
			)
			if (summaryType == 'calendar') {
				contentAttributes.exportValue = value
			}
			return value
		}

		if (!isNumericDataType(dataType) && !isDateTimeDataType(dataType) &&
			dataType !== YES_NO && dataType !== THRESHOLD_ID &&
			dataType !== 'score') {
			return value
		}

		// #41644 prevent empty settings from overriding values in 'attrs'
		let ignoreEmptyValues = (objValue, srcValue) => {
			return _.isNil(srcValue) ? objValue : srcValue
		}

		const formattingOptions = _.extendWith({}, resolvedAttrs, {
			value: _.escape(value),
			dataType
		}, ignoreEmptyValues)
		let val = shortenedAndUnshortened(formattingOptions)
		if (abbreviated && isNumericDataType(dataType)) {
			return toAbbreviatedResult(val.shortened, val.unshortened, resolvedAttrs)
		}
		return val.unshortened
	}

	_summaryValueFormattingContext(columnAttributes) {
		return {}
	}

	wrapGroupHeaderValue(value, columnAttributes) { return `<span>${value}</span>` }

	/**
	 * Captures the raw data associated to a report cell that can be used to
	 * fully build out the (excel) export model at the time an export is requested.
	 *
	 * Used by base-designer-results#exportModelLookup
	 */
	_storeExportModel(result, dataValue, contentAttributes = {}, overrides = {}) {
		let { dataType, precision, currencyCode, summaryType, exportValue,
			aggregated, aggregationType } = _.extend({}, contentAttributes, overrides)
		exportValue = exportValue || result.exportValue
		if (dataType == THRESHOLD_ID) {
			dataValue = formatValue({value: dataValue, dataType})
		}
		let value = !_.isNil(exportValue) ? exportValue : dataValue

		if (aggregated) {
			dataType = getAggregatedDataType(dataType, aggregationType)
		}

		let resolvedDataType = dataType
		if (dataType == DATE && summaryType && summaryType != 'value') {
			//#43498 #44155 if not using value as a summary type for dates
			//treat as text
			resolvedDataType = TEXT
		}
		if (dataType == DATASET_LINK) {
			resolvedDataType = contentAttributes.coercedDataType
		}

		let rawValue = _.get(result, 'rawValue', value)

		//#20258 define any conditional color to the cell
		let color = applyThresholdColoring(result, resolvedDataType, rawValue)
		if (color) {
			color = color.formatHex()
		}

		const timeFormat =
			overrides.timeFormat || contentAttributes.timeFormat
		result.exportModel = {
			value,
			htmlValue: result.html,
			dataType : resolvedDataType,
			precision,
			currencyCode,
			summaryType,
			timeFormat,
			color  //tell export about the custom color
		}
	}

	/**
	 * Takes a collection of values and transforms to a string suitable for exports
	 */
	_valuesToExportString(values, toString = _.identity) {
		return _(values)
			.filter(v => !!v)
			.map(v => toString(v))
			.join('\n')
	}
}

function formattedOverallColorHtml(value) {
	let color = _.kebabCase(value)
	let label = value ? getColorLabel(color) : null
	label = label === i18n.get('scorecards.color.gray.descr')
		? i18n.get('scorecards.color.gray') : label
	return label
}

/**
 * Formats the related items into an HTML string
 * @param relatedItem the related items, which has already been augmented by
 * related-items-list.js:formatItem
 */
function formattedRelatedItemHtml(relatedItem) {
	return _.isNil(relatedItem) ? '' : relatedItemTemplate(relatedItem)
}

function getColorLabel(color) {
	return i18n.get(enums.scoreColor[color || 'gray'])
}

export default ReportDataExtractor
export { toAbbreviatedResult, formattedOverallColorHtml, getColorLabel,
	formattedRelatedItemHtml }
ReportDataExtractor.__filename__ = 'reports/common/report-data-extractor.js'
